import React, { useState, useEffect, useContext, useMemo, useRef, useCallback } from 'react';
import { QuantityContext } from '../../hooks/quantityContext';
import { UpdateGroupVisibility, findRecursiveInTree } from '../../helpers/three';
import { useHttp } from '../../hooks/useHttp';
import { getJsonFile, getCompressedJsonFile } from '../../api/files/getJsonFile';
import { url as urlApi, IFC_Url } from '../../api';
import { buildWbsFromGenericSchedule } from '../../helpers/schedule/genericSchedule';
import localSchedule from '../../helpers/schedule/schedule.json';

// ADD these
import {
  HOURS_PER_DAY,
  sum,
  riskPctAtProgress,
  minCostWithTol,
  maxCostWithTol
} from '../../helpers/schedule/config';

import {
  attachAwpFromCatalog,
  buildWbsFromMeshes,
  groupBy,
} from '../../helpers/schedule/awp';

import { buildAwpSummaryFromDivisions } from '../../helpers/schedule/awp_summary';


import CollapsibleEstimate from '../CollapsibleEstimate';
import { syncSceneForDate } from '../../helpers/schedule/threeSync';

import iconEquip from "assets/images/icn-53-equipment.svg";
import iconLabor from "assets/images/icn-53-labor.svg";
import iconMat from "assets/images/icn-53-material.svg";
import iconMoney from "assets/images/icn-53-money.svg";
import iconTime from "assets/images/icn-53-time.svg";


import * as XLSX from 'xlsx';
import './gantt.scss';



// Fraction [0..1] of a task complete at a specific date
const progressAtDate = (task, date) => {
 if (!task || !task.startDate || !task.endDate) return 0;
  const s = +new Date(task.startDate);
  const e = +new Date(task.endDate);
  const d = +new Date(date);
  if (d <= s) return 0;
  if (d >= e) return 1;
  const span = e - s;
  return span ? (d - s) / span : 0;
};

// Risk for a task at a specific date (uses tolerance and progress)
const getTaskRisk = (task, date, awpMappingRef) => {
  const s = +new Date(task.startDate || 0);
  const e = +new Date(task.endDate || 0);
  const d = +new Date(date || 0);
  const p = (e > s) ? Math.max(0, Math.min(1, (d - s) / (e - s))) : 0;
var tol = 0;
if (typeof task.tolerancePct === 'number') {
  tol = task.tolerancePct;
} else if (
  task && task.isAWP && task.awpCode &&
  awpMappingRef && awpMappingRef[task.awpCode] &&
  typeof awpMappingRef[task.awpCode].tolerancePct === 'number'
) {
  tol = awpMappingRef[task.awpCode].tolerancePct;
}
  // riskPctAtProgress(tolerancePct, progress01) comes from schedule/config
  return riskPctAtProgress(tol, p);
};

// Aggregate AWP hours from Excel + mesh-derived hours
// Return total hours (labor+equipment) for an AWP code
const getAwpHours = (awpCode, { awpData, meshes }) => {
  let hours = 0;

  // Excel rows
  if (Array.isArray(awpData)) {
    for (const r of awpData) {
      if (r && r.awpId === awpCode) {
        // prefer laborHours if present, otherwise fallback to duration (treat as hours)
        hours += Number(r.laborHours) || Number(r.duration) || 0;
      }
    }
  }

  // Mesh rows (produced by extractMeshesWithAwp)
  if (Array.isArray(meshes)) {
    for (const m of meshes) {
      if (m && m.awpCode === awpCode) {
        hours +=
          (Number(m.laborHours) || 0) +
          (Number(m.equipHours) || 0);
      }
    }
  }

  return hours;
};

// Update AWP task durations (based on hours) and reflow siblings FS
const recalcAwpDurationsAndReflow = (wbs, { getHours, autoReflow = true }) => {
  // 1) Update durations / end dates based on hours
  const updateDurations = (nodes) =>
    nodes.map((t) => {
      let task = { ...t };

      if (task.isAWP && task.awpCode) {
        const hrs = Math.max(0, Number(getHours(task.awpCode)) || 0);
        const days = Math.max(1, Math.ceil(hrs / HOURS_PER_DAY));
        // keep startDate, recompute endDate/duration
        const start = task.startDate ? new Date(task.startDate) : new Date();
        const end = new Date(start);
        end.setDate(end.getDate() + days);
        task.duration = days;
        task.endDate = end;
      }

      if (Array.isArray(task.children) && task.children.length) {
        task.children = updateDurations(task.children);
      }
      return task;
    });

  let updated = updateDurations(wbs);

  // 2) Reflow siblings: each task starts when the previous one ends (FS1)
  if (autoReflow) {
    const reflow = (nodes) => {
      let cursor = null;
      return nodes.map((t) => {
        let task = { ...t };
        const hasDates = task.startDate && task.endDate && task.duration;
        if (!hasDates) return task;

        // if it starts before the current cursor, shift to cursor
        if (cursor && new Date(task.startDate) < cursor) {
          const start = new Date(cursor);
          const end = new Date(start);
          end.setDate(end.getDate() + (Number(task.duration) || 0));
          task.startDate = start;
          task.endDate = end;
        }

        if (Array.isArray(task.children) && task.children.length) {
          task.children = reflow(task.children);
        }

        cursor = new Date(task.endDate);
        return task;
      });
    };

    updated = reflow(updated);
  }

  return updated;
};


// compute to-date labor/equipment/material for active tasks
const computeActiveEstimatesToDate = (active, date) => {
  const labor = [];
  const equip = [];
  const material = [];
  let totalSelectionTime = 0;
  let totalSelectionPrice = 0;

  active.forEach(task => {
    const frac = progressAtDate(task, date);

    if (Array.isArray(task.laborInfo)) {
      task.laborInfo.forEach(l => {
        labor.push({
          Code: task.name,
          Description: l.Description,
          TotalTime: (Number(l.TotalTime) || 0) * frac,
          TotalCost: (Number(l.TotalPrice) || 0) * frac
        });
      });
    }
    if (Array.isArray(task.equipmentInfo)) {
      task.equipmentInfo.forEach(e => {
        equip.push({
          Code: task.name,
          Description: e.Description,
          TotalTime: (Number(e.TotalTime) || 0) * frac,
          TotalCost: (Number(e.TotalPrice) || 0) * frac
        });
      });
    }
    if (Array.isArray(task.materialInfo)) {
      task.materialInfo.forEach(m => {
        material.push({
          Code: task.name,
          Description: Array.isArray(m.Types) ? m.Types.join(' | ') : (m.Name || 'Material'),
          TotalTime: 0,
          TotalCost: (Number(m.TotalPrice) || 0) * frac
        });
      });
    }

    totalSelectionPrice += (Number(task.totalPrice) || 0) * frac;
  });

  const laborHrs = sum(labor.map(x => x.TotalTime));
  const equipHrs = sum(equip.map(x => x.TotalTime));

  return {
    labor, equip, material,
    totalSelectionTime: laborHrs + equipHrs,
    totalSelectionPrice
  };
};


// Build a minimal “mesh rows” view using attachAwpFromCatalog() output.
// This gives us awpCode + (labor/equip) hours for each mesh, which is
// exactly what getAwpHours() needs.
const extractMeshesFromState = (data) => {
  const { rows = [] } = attachAwpFromCatalog(data || {});
  return rows.map(r => ({
    meshId: r.meshId,
    awpCode: r.awpCode,
    laborHours: Number(r.laborHours || 0),
    equipHours: Number(r.equipHours || 0),
  }));
};





const GanttChart = ({ timeline, rule, WebGL, clickedChild, treeData, setTreeData, project }) => {
  const { Currency, meshQuantityInfoRef } = useContext(QuantityContext);
  const [tasks, setTasks] = useState([]);
  const [selectedTask, setSelectedTask] = useState(null);
  const [viewMode, setViewMode] = useState('week');
  const [startDate, setStartDate] = useState(new Date('2024-01-01'));
  const ganttRef = useRef(null);
  const timelineRef = useRef(null);
  const sidebarRef = useRef(null);
  const [hoveredTask, setHoveredTask] = useState(null);
  const [filterMode, setFilterMode] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredTasks, setFilteredTasks] = useState([]);
  const [excelData, setExcelData] = useState(null);
  const [awpData, setAwpData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [jsonData, setJsonData] = useState(null);
  const [wbsStructure, setWbsStructure] = useState([]);
  const [awpMapping, setAwpMapping] = useState({});
  
  // Store processed mesh data
  const [processedMeshData, setProcessedMeshData] = useState({
    labor: [],
    equipment: [],
    material: []
  });
  
  // Timeline animation states
  const [currentDate, setCurrentDate] = useState(new Date('2024-01-01'));
  const [isPlaying, setIsPlaying] = useState(false);
  const [animationSpeed, setAnimationSpeed] = useState(1);
  const [totalSpent, setTotalSpent] = useState(0);
  const [activeTasks, setActiveTasks] = useState([]);
  const animationRef = useRef(null);
  
  // Recording states
  const [isRecording, setIsRecording] = useState(false);
  const recordedFrames = useRef([]);
  const canvasRef = useRef(null);
  
  // Responsive states
  const [isMobile, setIsMobile] = useState(false);
  const [sidebarWidth, setSidebarWidth] = useState(400);
  const [isResizing, setIsResizing] = useState(false);
  
  // Filter states
  const [hiddenCategories, setHiddenCategories] = useState(new Set());
  const [activeRules, setActiveRules] = useState({});


  const [scheduleData, setScheduleData] = useState(null);

  
  // AWP Rules for animation
  const awpRules = {
    '3A': {
      name: 'Site Excavation Rules',
      showSequence: ['Excavation', 'Site', 'Foundation'],
      hideOthers: true,
      animationDelay: 100,
      priority: 1
    },
    '4A': {
      name: 'Concrete Pour Rules',
      showSequence: ['Concrete', 'Foundation', 'Formwork', 'Rebar'],
      hideOthers: true,
      animationDelay: 200,
      priority: 2
    },
    '5A': {
      name: 'Steel Erection Rules',
      showSequence: ['Steel', 'Structure', 'Columns', 'Beams'],
      hideOthers: true,
      animationDelay: 150,
      priority: 3
    }
  };

  // Process mesh data from QuantityContext when selection changes
useEffect(() => {
  if (meshQuantityInfoRef && meshQuantityInfoRef.current) {
    processMeshQuantityData();
  }
}, [meshQuantityInfoRef, clickedChild]);

  // Process mesh quantity data using the same logic as provided
  const processMeshQuantityData = () => {
    if (!meshQuantityInfoRef || !meshQuantityInfoRef.current) return;
    
    const laborData = [];
    const equipData = [];
    const matData = [];
    let totalSelectionTime = 0;
    let totalSelectionPrice = 0;
    
    meshQuantityInfoRef.current.forEach(mesh => {
      let meshTime = 0;
      
      // Process Labor Info
      if (mesh.LaborInfo && mesh.LaborInfo.length > 0) {
        mesh.LaborInfo.forEach(l => {
          laborData.push({
            TotalCost: l.TotalPrice,
            TotalTime: l.TotalTime,
            Code: `${mesh.Name}-${mesh.Type}`,
            Description: l.Description,
            meshId: mesh.UniqueId
          });
          meshTime += Number(l.TotalTime || 0);
        });
      }
      
      // Process Equipment Info
      if (mesh.EquipmentInfo && mesh.EquipmentInfo.length > 0) {
        mesh.EquipmentInfo.forEach(info => {
          equipData.push({
            TotalCost: info.TotalPrice,
            TotalTime: info.TotalTime,
            Code: mesh.Name,
            Description: info.Description,
            meshId: mesh.UniqueId
          });
        });
      }
      
      // Process Material Info
      matData.push({
        TotalCost: mesh.TotalPrice ? Number(mesh.TotalPrice) : 0,
        TotalTime: meshTime ? Number(meshTime) : 0,
        Code: mesh.Name,
        Description: Array.isArray(mesh.MaterialNames) ? mesh.MaterialNames.join(" | ") : undefined,
        meshId: mesh.UniqueId,
        mesh: mesh // Store full mesh data
      });
      
      totalSelectionTime += meshTime;
      totalSelectionPrice += Number(mesh.TotalPrice || 0);
    });
    
    setProcessedMeshData({
      labor: laborData,
      equipment: equipData,
      material: matData,
      totalTime: totalSelectionTime,
      totalPrice: totalSelectionPrice
    });
    
    // Update WBS structure with new data
    if (jsonData) {
      processJsonDataToWBS(jsonData);
    }
  };

  // Load JSON data
  const { request: loadJsonData } = useHttp({
    requestCallback: async () => {
      if (!timeline || !timeline.json) return null;
      
      try {
        const data = await getJsonFile(timeline.json);
        return data;
      } catch (error) {
        console.error('Error loading JSON data:', error);
        return null;
      }
    },
    onLoad: (data) => {
      if (data) {
        setJsonData(data);
        extractAWPMapping(data);
        processJsonDataToWBS(data);
      }
      setIsLoading(false);
    },
    onError: (error) => {
      console.error('Error loading JSON:', error);
      setIsLoading(false);
    }
  });

const clearSelection = useCallback(() => {
  setSelectedTask(null);
  // Optional: re-sync scene so any glow/highlight is cleared
var v = WebGL && WebGL.current && WebGL.current.viewer;
if (v) {
  try { syncSceneForDate(v, tasks, currentDate); } catch (e) {}
}
}, [WebGL, tasks, currentDate]);


const { request: loadScheduleData } = useHttp({
  requestCallback: async () => {
    // Prefer a runtime-provided path
    if (timeline && timeline.schedule) {
      try {
        const remote = await getJsonFile(timeline.schedule);
        // Must be an array; otherwise fall back
        return Array.isArray(remote) ? remote : localSchedule;
      } catch (err) {
        console.error('Error loading schedule.json (remote):', err);
        return localSchedule;
      }
    }
    // No timeline path -> use helper JSON
    return localSchedule;
  },
  onLoad: (data) => {
    if (Array.isArray(data)) setScheduleData(data);
    else setScheduleData(Array.isArray(localSchedule) ? localSchedule : []);
  },
  onError: (e) => {
    console.error('Error loading schedule.json:', e);
    setScheduleData(Array.isArray(localSchedule) ? localSchedule : []);
  }
});

// Auto-spin camera while the timeline is playing
useEffect(() => {
var v = WebGL && WebGL.current && WebGL.current.viewer;
if (!v) return;
if (isPlaying && v.startAutoSpin) v.startAutoSpin();
else if (v.stopAutoSpin) v.stopAutoSpin();

}, [isPlaying, WebGL]);

// HOISTED helper (safe to reference before other consts)
function getVisibleIdsForDate(tasks, date) {
  const ids = [];
  const pushAll = (t) => {
    if (t.meshIds) ids.push(...t.meshIds);
    else if (t.meshId) ids.push(t.meshId);
  };
  for (const t of tasks) {
    if (!t || !t.startDate || !t.endDate) continue;
    if (t.startDate <= date) pushAll(t);
  }
  return Array.from(new Set(ids));
}


// Initialize the 3D viewer for schedule playback

useEffect(() => {
  const v = WebGL && WebGL.current && WebGL.current.viewer;
  if (!v) return;

  if (typeof v.enableProgressMode === 'function') {
    v.enableProgressMode({ startHidden: true });
  }

  const idsForStart = getVisibleIdsForDate(tasks, startDate);
  if (typeof v.setVisibleByProgress === 'function') {
    v.setVisibleByProgress(idsForStart, { accumulate: false, frame: false });
  }

  return () => {
    if (typeof v.stopAutoSpin === 'function') v.stopAutoSpin();
  };
}, [WebGL, startDate, tasks]);



  // Responsive handling
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      if (window.innerWidth < 768) {
        setSidebarWidth(window.innerWidth);
      } else {
        setSidebarWidth(400);
      }
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Sidebar resize handling
  const handleMouseDown = (e) => {
    setIsResizing(true);
    e.preventDefault();
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizing) return;
      
      const newWidth = e.clientX;
      if (newWidth > 200 && newWidth < 600) {
        setSidebarWidth(newWidth);
      }
    };
    
    const handleMouseUp = () => {
      setIsResizing(false);
    };
    
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }
    
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  // Load data on mount
  useEffect(() => {
    if (timeline) {
      loadJsonData();
      loadExcelData();
       loadScheduleData(); // <-- add this
    }
  }, [timeline]);

  // Extract AWP mapping
  const extractAWPMapping = (data) => {
    const mapping = {};
    
    if (data.Crews) {
      data.Crews.forEach(crew => {
        if (crew.AWP && crew.AWP.length > 0) {
          crew.AWP.forEach(awpCode => {
            if (!mapping[awpCode]) {
              mapping[awpCode] = {
                code: awpCode,
                description: crew.Description,
                crews: [],
                components: [],
                totalHours: 0,
                totalCost: 0,
                rules: awpRules[awpCode] || {}
              };
            }
            mapping[awpCode].crews.push({
              code: crew.Code,
              description: crew.Description,
              yield: crew.Yield,
              yieldUnit: crew.YieldUnit,
              labors: crew.Labors
            });
          });
        }
      });
    }
    
    setAwpMapping(mapping);
  };

  const processJsonDataToWBS = (data) => {
  if (!data) return;

  // 1) Build AWP summary (cost/tolerance) from Divisions
  const { byCode: awpSummaryByCode } = buildAwpSummaryFromDivisions(data, project) || { byCode: {} };

  // 2) Attach AWP to meshes via catalog matching
  const { rows = [] } = attachAwpFromCatalog(data) || { rows: [] };
  if (!rows.length) {
    console.warn('[Gantt] No meshes found to schedule.');
    setWbsStructure([]); setTasks([]); return;
  }

  // 3) Excel AWP durations fallback
  const awpDurationHoursByCode = {};
  if (awpData && Array.isArray(awpData)) {
    awpData.forEach((r) => {
      if (!r?.awpId) return;
      const hours = Number(r.duration) || 0;
      if (hours > 0) awpDurationHoursByCode[r.awpId] = hours;
    });
  }

  const normalizedRows = rows.map((r) => {
    let durationDays = r.durationDays;
    const totalHrs = (Number(r.laborHours || 0) + Number(r.equipHours || 0));
    if (totalHrs <= 0) {
      const hours = awpDurationHoursByCode[r.awpCode] || 0;
      if (hours > 0) durationDays = Math.max(1, Math.ceil(hours / HOURS_PER_DAY));
    }
    return {
      meshId: r.meshId,
      awpCode: r.awpCode,
      laborHours: Number(r.laborHours || 0),
      equipHours: Number(r.equipHours || 0),
      totalCost: Number(r.totalCost || 0),
      durationDays
    };
  });

  // 4) Build AWP mapping (quick jump menu / tooltips)
  const grouped = groupBy(normalizedRows, 'awpCode');
  const mapping = {};
  Object.keys(grouped).forEach((code) => {
    const items = grouped[code] || [];
    const s = (awpSummaryByCode && awpSummaryByCode[code]) || {};
    mapping[code] = {
      code,
      description: s.description || `Area Work Package ${code}`,
      crews: [],
      components: items.map((i) => i.meshId),
      totalHours: sum(items.map((i) => (Number(i.laborHours||0) + Number(i.equipHours||0)))),
      totalCost: (typeof s.total === 'number' ? s.total : sum(items.map((i) => Number(i.totalCost || 0)))),
      rules: awpRules[code] || {},
      tolerancePct: typeof s.tolerancePct === 'number' ? s.tolerancePct : 0,
      minTotal: s.minTotal,
      maxTotal: s.maxTotal
    };
  });
  setAwpMapping(mapping);

  // 5) USE GENERIC SCHEDULE if provided; otherwise fallback to mesh-driven WBS
  if (scheduleData && Array.isArray(scheduleData) && scheduleData.length > 0) {
    const wbs = buildWbsFromGenericSchedule(scheduleData, startDate, grouped, awpSummaryByCode);
    setWbsStructure(wbs);
    setTasks(flattenTasks(wbs));
    return;
  }

  // Fallback: your existing mesh-driven build
  const wbs = buildWbsFromMeshes(normalizedRows, startDate, awpSummaryByCode);
  setWbsStructure(wbs);
  setTasks(flattenTasks(wbs));
};






  // Helper functions


  // SHOW every mesh in the viewer and frame the whole scene
const showEntireScene = useCallback(() => {
  const v = WebGL?.current?.viewer;
  if (!v) return;

  // 1) make all groups visible
  if (v.meshesObject && v.meshesObject.size) {
    for (const [, grp] of v.meshesObject.entries()) {
      UpdateGroupVisibility(grp, true);
      // if you used packed-instance visibility, fully reset it:
      if (grp.mesh?.isInstancedMesh && v.resetInstancedMeshVisibilityPacked) {
        v.resetInstancedMeshVisibilityPacked(grp.mesh);
      }
    }
  }

  // 2) tell progress system “everything is visible”
  if (v.setVisibleByProgress) {
    const allIds = v.meshesObject ? Array.from(v.meshesObject.keys()) : [];
    v.setVisibleByProgress(allIds, { accumulate: false, frame: true });
  }

  // 3) stop auto-spin if it’s on
  if (v.stopAutoSpin) v.stopAutoSpin();
}, [WebGL]);

// Clear any opacity/emissive tweaks (future/current states) and set to solid
const resetAllMeshMaterials = useCallback(() => {
  const v = WebGL?.current?.viewer;
  if (!v || !v.meshesObject) return;

  const restore = (obj) => {
    obj.traverse((child) => {
      if (!child?.material) return;
      const mats = Array.isArray(child.material) ? child.material : [child.material];
      for (const m of mats) {
        if (!m) continue;
        if ('emissive' in m) {
          m.emissive.setHex(0x000000);
          m.emissiveIntensity = 0;
        }
        m.transparent = false;
        m.opacity = 1;
        m.needsUpdate = true;
      }
    });
  };

  for (const [, grp] of v.meshesObject.entries()) {
    if (grp.mesh) restore(grp.mesh);
  }
}, [WebGL]);



  const addDays = (date, days) => {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
  };

  const flattenTasks = (wbsTasks, parentVisible = true) => {
    const flattened = [];
    
    wbsTasks.forEach(task => {
      const taskVisible = parentVisible && task.visible && !hiddenCategories.has(task.category);
      flattened.push({ ...task, visible: taskVisible });
      
      if (task.children && task.expanded) {
        flattened.push(...flattenTasks(task.children, taskVisible));
      }
    });
    
    return flattened;
  };

  // === Viewer bridge (gantt ⇄ 3D) ==========================================
const meshIdsOf = (t) => t.meshIds || (t.meshId ? [t.meshId] : []);
const uniq = (arr) => Array.from(new Set(arr));

// All meshes that should be visible at a given date (past + current)
const visibleIdsForDate = useCallback((date) => {
  const ids = [];
  tasks.forEach((t) => {
    if (!t.startDate || !t.endDate) return;
    if (t.startDate <= date) ids.push(...meshIdsOf(t));
  });
  return uniq(ids);
}, [tasks]);

// All meshes that are currently "visible" per task flags/category filters
const visibleIdsFromFlags = useCallback(() => {
  const ids = [];
  const include = (t) => t.visible && !hiddenCategories.has(t.category);
  const walk = (nodes) => {
    nodes.forEach((t) => {
      if (include(t)) ids.push(...meshIdsOf(t));
      if (Array.isArray(t.children) && t.children.length) walk(t.children);
    });
  };
  walk(wbsStructure || []);
  return uniq(ids);
}, [wbsStructure, hiddenCategories]);
// ========================================================================

  // Apply AWP rules during animation
// make this PURE: no UpdateGroupVisibility here, no setTasks inside
const idsAllowedByAwpRule = (rule, tasks) => {
  if (!rule) return null;
  const seq = (rule.showSequence || []).map(s => s.toLowerCase());

  const allowed = new Set();
  for (const t of tasks) {
    const name = ((t.category || '') + ' ' + (t.name || '')).toLowerCase();
    const matches = t.isAWP || seq.some(s => name.includes(s));
    if (matches) {
      const ids = t.meshIds || (t.meshId ? [t.meshId] : []);
      ids.forEach(id => allowed.add(id));
    }
  }
  return Array.from(allowed);
};



  // === 4.5 Dynamic 4D/5D helpers — PLACE ABOVE updateTimelinePosition ===

// Cost accrued for a task up to `date` (linear across its duration)
const costToDate = (task, date) => {
if (!task || !task.startDate || !task.endDate) return 0;
  if (date <= task.startDate) return 0;
  if (date >= task.endDate) return task.totalPrice || 0;
  const span = task.endDate - task.startDate;
  const elapsed = date - task.startDate;
  const p = Math.max(0, Math.min(1, span ? (elapsed / span) : 0));
  return (task.totalPrice || 0) * p;
};

// Visual states for meshes in the 3D viewer
const setMeshOpacity = (meshObject, alpha = 1) => {
  try {
    if (!meshObject || typeof meshObject.traverse !== 'function') return;

    meshObject.traverse((child) => {
      if (!child || !child.material) return;

      const mats = Array.isArray(child.material) ? child.material : [child.material];
      for (let i = 0; i < mats.length; i++) {
        const m = mats[i];
        if (!m) continue;
        m.transparent = alpha < 1;
        m.opacity = alpha;
        m.needsUpdate = true;
      }
    });
  } catch (e) {}
};


const setMeshGlow = (meshObject, on = false) => {
  try {
    if (!meshObject || typeof meshObject.traverse !== 'function') return;

    meshObject.traverse((child) => {
      if (!child || !child.material) return;

      const mats = Array.isArray(child.material) ? child.material : [child.material];
      for (let i = 0; i < mats.length; i++) {
        const mat = mats[i];
        if (!mat) continue;
        if ('emissive' in mat) {
          mat.emissive.setHex(on ? 0xffff00 : 0x000000);
          mat.emissiveIntensity = on ? 0.7 : 0;
          mat.needsUpdate = true;
        }
      }
    });
  } catch (e) {}
};


// Apply one of three states: 'past' (solid), 'current' (glow), 'future' (transparent)
const applyMeshState = (id, state) => {
var v = WebGL && WebGL.current && WebGL.current.viewer;
if (!v || !v.meshesObject) return;
var meshObject = v.meshesObject.get(id);

  if (!meshObject) return;

  if (state === 'past') {
    setMeshGlow(meshObject, false);
    setMeshOpacity(meshObject, 1);
  } else if (state === 'current') {
    setMeshGlow(meshObject, true);
    setMeshOpacity(meshObject, 1);
  } else {
    setMeshGlow(meshObject, false);
    setMeshOpacity(meshObject, 0.25);
  }
};


  // // Timeline animation with AWP rules
  // const updateTimelinePosition = useCallback((date) => {
  //   setCurrentDate(date);
    
  //   // Calculate active tasks and spent money
  //   const active = [];
  //   let spent = 0;
  //   let currentAwp = null;
    
  //   tasks.forEach(task => {
  //     if (task.startDate <= date && task.endDate >= date) {
  //       active.push(task);
        
  //       // Check if this is an AWP
  //       if (task.isAWP && task.awpCode) {
  //         currentAwp = task.awpCode;
  //       }
        
  //       // Calculate prorated spending
  //       const taskProgress = (date - task.startDate) / (task.endDate - task.startDate);
  //       spent += (task.totalPrice || 0) * taskProgress;
  //     } else if (task.endDate < date) {
  //       spent += task.totalPrice || 0;
  //     }
  //   });
    
  //   setActiveTasks(active);
  //   setTotalSpent(spent);
    
  //   // Apply AWP rules if playing
  //   if (isPlaying && currentAwp) {
  //     applyAWPRules(date, currentAwp);
  //   }
  // }, [tasks, isPlaying, applyAWPRules]);

const updateTimelinePosition = useCallback((date) => {
  setCurrentDate(date);

  const active = [];
  let spent = 0;
  let currentAwp = null;

  // cost & risk
  let totalRiskWeighted = 0;
  let riskDen = 0;

  tasks.forEach(task => {
    const ids = task.meshIds || (task.meshId ? [task.meshId] : []);
    const isWithin = task.startDate <= date && task.endDate >= date;
    const isPast = task.endDate < date;
    const isFuture = task.startDate > date;

    // cost to date
    spent += costToDate(task, date);

    if (isWithin) {
      active.push(task);
      if (task.isAWP && task.awpCode) currentAwp = task.awpCode;
    }

    // risk
    const span = task.endDate - task.startDate;
    const elapsed = Math.max(0, date - task.startDate);
    const progress01 = Math.max(0, Math.min(1, span ? elapsed / span : 0));
    const tol = task.tolerancePct || (task.isAWP ? (awpMapping?.[task.awpCode]?.tolerancePct || 0) : 0);
    const riskNow = riskPctAtProgress(tol, progress01);
    totalRiskWeighted += riskNow;
    riskDen += 1;

    // 3D state
    ids.forEach((id) => applyMeshState(id, isPast ? 'past' : isFuture ? 'future' : 'current'));
  });

  setActiveTasks(active);
  setTotalSpent(spent);

  // Decide visibility ONCE using progress + (optional) AWP filter
  const v = WebGL?.current?.viewer;
  if (v?.setVisibleByProgress) {
    const baseIds = visibleIdsForDate(date); // everything built up to `date`
    const rule = currentAwp ? awpRules[currentAwp] : null;

    if (rule && rule.hideOthers) {
      const allowed = idsAllowedByAwpRule(rule, tasks) || [];
      const filtered = baseIds.filter(id => allowed.includes(id));
      v.setVisibleByProgress(filtered, { accumulate: false, frame: false });
    } else {
      v.setVisibleByProgress(baseIds, { accumulate: false, frame: false });
    }

    if (isPlaying && v.startAutoSpin) v.startAutoSpin();
  }

  const avgRisk = riskDen ? totalRiskWeighted / riskDen : 0;
  setActiveRules({ ...(currentAwp ? { [currentAwp]: awpRules[currentAwp] } : {}), riskPctNow: avgRisk });

  // ❌ DO NOT call applyAWPRules here anymore.
}, [tasks, isPlaying, awpMapping, WebGL, visibleIdsForDate, idsAllowedByAwpRule]);



useEffect(() => {
  if (jsonData) processJsonDataToWBS(jsonData);
}, [awpData, scheduleData]); // <- keep this

  //recalc durations whenever hours change

useEffect(() => {
  if (!wbsStructure || !wbsStructure.length) return;

  // let helpers compute hours using both Excel and mesh data
  const getHours = (code) => getAwpHours(code, {
    awpData,
    meshes: (jsonData ? extractMeshesFromState(jsonData) : [])
  });

  // NOTE: we’ll define extractMeshesFromState just below
  const updated = recalcAwpDurationsAndReflow(wbsStructure, {
    getHours,
    autoReflow: true
  });

  setWbsStructure(updated);
  setTasks(flattenTasks(updated));
}, [awpData, processedMeshData, jsonData]); // any time sources change

  // Animation loop
  useEffect(() => {
    if (isPlaying) {
      animationRef.current = setInterval(() => {
        setCurrentDate(prevDate => {
          const nextDate = addDays(prevDate, animationSpeed);
          const endDate = tasks.length > 0 ? 
            new Date(Math.max(...tasks.filter(t => t.endDate).map(t => t.endDate))) : 
            new Date();
          
          // if (nextDate > endDate) {
          //   setIsPlaying(false);
          //   // Reset visibility when animation ends
          //   resetAllVisibility();
          //   return prevDate;
          // }
          
          if (nextDate >= endDate) {
            // Snap to the final date so the pointer lands at the end
            updateTimelinePosition(endDate);
            // Stop playback
            setIsPlaying(false);
            // Clear any transparency/glow and reveal everything
            resetAllMeshMaterials();
            showEntireScene();
            return endDate;
     }

          updateTimelinePosition(nextDate);
          
          if (isRecording && canvasRef.current) {
            captureFrame();
          }
          
          return nextDate;
        });
      }, 100);
    } else {
      if (animationRef.current) {
        clearInterval(animationRef.current);
      }
    }
    
    return () => {
      if (animationRef.current) {
        clearInterval(animationRef.current);
      }
    };
  }, [isPlaying, animationSpeed, updateTimelinePosition, isRecording]);


//Compute average risk for the header status strip:

  const activeRisk = useMemo(() => {
  const awpActive = activeTasks.filter(t => t.isAWP);
  if (!awpActive.length) return 0;
  const r = awpActive
    .map(t => getTaskRisk(t, currentDate))
    .reduce((a,b)=>a+b,0) / awpActive.length;
  return r;
}, [activeTasks, currentDate]);



  // Recording functions
  const captureFrame = () => {
    if (!ganttRef.current || !canvasRef.current) return;
    
    recordedFrames.current.push({
      date: new Date(currentDate),
      spent: totalSpent,
      activeTasks: [...activeTasks],
      activeRules: { ...activeRules }
    });
  };

  const exportVideo = () => {
    console.log('Exporting video with', recordedFrames.current.length, 'frames');
    alert('Video export would be implemented with FFmpeg.js or server-side processing');
  };

  const exportGif = () => {
    console.log('Exporting GIF with', recordedFrames.current.length, 'frames');
    alert('GIF export would be implemented with gif.js library');
  };

  // Load Excel data
  const loadExcelData = async () => {
    if (!timeline) return;
    
    try {
      const excelPromises = [];
      
      if (timeline.excelAwp) {
        excelPromises.push(
          fetch(`${urlApi}/${timeline.excelAwp}`)
            .then(res => res.blob())
            .then(blob => blob.arrayBuffer())
            .then(data => {
              const workbook = XLSX.read(data);
              return parseAWPData(workbook);
            })
        );
      }
      
      const results = await Promise.all(excelPromises);
      
      if (results[0]) {
        setAwpData(results[0]);
      }
      
    } catch (error) {
      console.error('Error loading Excel data:', error);
    }
  };

  // Parse AWP data
  const parseAWPData = (workbook) => {
    const awpTasks = [];
    
    if (workbook.Sheets[' AWP Material']) {
      const sheet = workbook.Sheets[' AWP Material'];
      const data = XLSX.utils.sheet_to_json(sheet, { header: 1 });
      
      data.forEach((row, idx) => {
        if (row[0] && row[2] && idx > 5) {
          awpTasks.push({
            awpId: row[0],
            description: row[2],
            duration: parseFloat(row[3]) || 8,
            laborHours: parseFloat(row[4]) || 0,
            materialCost: parseFloat(row[6]) || 0,
            totalCost: parseFloat(row[8]) || 0
          });
        }
      });
    }
    
    return awpTasks;
  };

  useEffect(() => {
  const onKeyDown = (e) => {
    if (e.key === 'Escape') clearSelection();
  };
  window.addEventListener('keydown', onKeyDown);
  return () => window.removeEventListener('keydown', onKeyDown);
}, [clearSelection]);


  // Filter tasks with category filtering
  useEffect(() => {
    let filtered = tasks;

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(task => 
        task.name.toLowerCase().includes(query) ||
        task.wbsCode.toLowerCase().includes(query) ||
        (task.type && task.type.toLowerCase().includes(query)) ||
        (task.category && task.category.toLowerCase().includes(query)) ||
        (task.isAWP && ('awp'.includes(query) || task.wbsCode.includes(query)))
      );
    }

    // Apply visibility filter
    switch (filterMode) {
      case 'visible':
        filtered = filtered.filter(task => task.visible);
        break;
      case 'hidden':
        filtered = filtered.filter(task => !task.visible);
        break;
      case 'wbs':
        filtered = filtered.filter(task => !task.isAWP);
        break;
      case 'awp':
        filtered = filtered.filter(task => task.isAWP || task.parentId?.startsWith('awp-'));
        break;
    }

    // Apply category filter
    filtered = filtered.filter(task => !hiddenCategories.has(task.category));

    setFilteredTasks(filtered);
  }, [tasks, searchQuery, filterMode, hiddenCategories]);

  // Get color based on category
  const getTaskColor = (category) => {
    const colors = {
      'Foundation': '#95a5a6',
      'Concrete': '#e67e22',
      'Steel': '#8e44ad',
      'Structure': '#34495e',
      'Excavation': '#e74c3c',
      'Site': '#16a085'
    };
    
    if (!category) return '#7f8c8d';
    
    for (const [key, color] of Object.entries(colors)) {
      if (category.toLowerCase().includes(key.toLowerCase())) {
        return color;
      }
    }
    return '#7f8c8d';
  };

  // Calculate grid dates
  const getGridDates = () => {
    const dates = [];
    const endDate = tasks.length > 0 ? 
      new Date(Math.max(...tasks.filter(t => t.endDate).map(t => t.endDate))) : 
      addDays(startDate, 180);
    const currentDate = new Date(startDate);
    
    while (currentDate <= endDate) {
      dates.push(new Date(currentDate));
      
      switch (viewMode) {
        case 'day':
          currentDate.setDate(currentDate.getDate() + 1);
          break;
        case 'week':
          currentDate.setDate(currentDate.getDate() + 7);
          break;
        case 'month':
          currentDate.setMonth(currentDate.getMonth() + 1);
          break;
      }
    }
    
    return dates;
  };

  // Calculate task position
  const calculateTaskPosition = (task) => {
    if (!task.startDate || !task.endDate) return { left: '0%', width: '0%' };
    
    const totalDays = tasks.length > 0 ? 
      Math.ceil((Math.max(...tasks.filter(t => t.endDate).map(t => t.endDate)) - startDate) / (1000 * 60 * 60 * 24)) : 180;
    const taskStart = Math.ceil((task.startDate - startDate) / (1000 * 60 * 60 * 24));
    const taskDuration = Math.ceil((task.endDate - task.startDate) / (1000 * 60 * 60 * 24));
    
    const left = Math.max(0, (taskStart / totalDays) * 100);
    const width = Math.max(0.5, (taskDuration / totalDays) * 100);
    
    return { left: `${left}%`, width: `${width}%` };
  };

  // Calculate timeline pointer position
  const calculateTimelinePosition = () => {
    const totalDays = tasks.length > 0 ?
      Math.ceil((Math.max(...tasks.filter(t => t.endDate).map(t => t.endDate)) - startDate) / (1000 * 60 * 60 * 24)) : 180;
    const currentDays = Math.ceil((currentDate - startDate) / (1000 * 60 * 60 * 24));
    return Math.max(0, Math.min(100, (currentDays / totalDays) * 100));
  };

  // Handle timeline drag
  const handleTimelineDrag = (e) => {
    if (!timelineRef.current) return;
    
    const rect = timelineRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = x / rect.width;
    
    const totalDays = tasks.length > 0 ?
      Math.ceil((Math.max(...tasks.filter(t => t.endDate).map(t => t.endDate)) - startDate) / (1000 * 60 * 60 * 24)) : 180;
    const days = Math.floor(percentage * totalDays);
    const newDate = addDays(startDate, days);
    
    updateTimelinePosition(newDate);
  };

  // Toggle task expansion
  const toggleTaskExpansion = (taskId) => {
    const updateExpansion = (tasks) => {
      return tasks.map(task => {
        if (task.id === taskId) {
          return { ...task, expanded: !task.expanded };
        }
        if (task.children) {
          return { ...task, children: updateExpansion(task.children) };
        }
        return task;
      });
    };
    
    const updatedWbs = updateExpansion(wbsStructure);
    setWbsStructure(updatedWbs);
    setTasks(flattenTasks(updatedWbs));
  };

  // Toggle task visibility
  const toggleTaskVisibility = (task) => {
    const updateVisibility = (tasks) => {
      return tasks.map(t => {
        if (t.id === task.id) {
          t.visible = !t.visible;
          
          if (WebGL?.current?.viewer && t.meshId) {
            const meshObject = WebGL.current.viewer.meshesObject.get(t.meshId);
            if (meshObject) {
              UpdateGroupVisibility(meshObject, t.visible);
            }
          }
          
          if (treeData && setTreeData && t.meshId) {
            const temp = [...treeData];
            const treeItem = findRecursiveInTree(temp, t.meshId);
            if (treeItem?.result) {
              treeItem.result.treeVisible = t.visible;
              setTreeData(temp);
            }
          }
        }
        
        if (t.children) {
          t.children = updateVisibility(t.children);
        }
        
        return t;
      });
    };
    
    const updatedWbs = updateVisibility([...wbsStructure]);
    setWbsStructure(updatedWbs);
    setTasks(flattenTasks(updatedWbs));

    // Sync 3D with current flags
var _v = WebGL && WebGL.current && WebGL.current.viewer;
if (_v && _v.setVisibleByProgress) {
  _v.setVisibleByProgress(visibleIdsFromFlags(), { accumulate:false, frame:false });
}


  };

  // Handle task click
const handleTaskClick = (task) => {
  // Toggle panel if the same task is clicked
  if (selectedTask?.id === task.id) {
    clearSelection();
    return;
  }

  setSelectedTask(task);

  const ids = meshIdsOf(task);

  // Select + frame in the viewer (keeps your highlight logic)
  if (WebGL?.current?.viewer) {
    // Your existing visual select
    ids.forEach((id) => {
      WebGL.current.viewer.onClickChildInList(
        id,
        false,
        { prevMeshes: 'prevMeshes', prevMaterials: 'prevMaterials', color: 'blue' },
        false,
        true
      );
    });

    // Make ONLY this task's meshes visible and frame them
   if (WebGL && WebGL.current && WebGL.current.viewer && WebGL.current.viewer.setVisibleByProgress) {
  WebGL.current.viewer.setVisibleByProgress(ids, { accumulate:false, frame:true });
}

  }
};




  // Format date
  const formatDate = (date) => {
    if (!date) return '';
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: viewMode === 'month' ? 'numeric' : undefined
    });
  };

  // Show/Hide by category
  const toggleCategoryVisibility = (category) => {
    const newHiddenCategories = new Set(hiddenCategories);
    
    if (newHiddenCategories.has(category)) {
      newHiddenCategories.delete(category);
    } else {
      newHiddenCategories.add(category);
    }
    
    setHiddenCategories(newHiddenCategories);
    
    // Update 3D visibility for affected components
    const updateVisibilityForCategory = (tasks) => {
      tasks.forEach(task => {
        if (task.category === category) {
          const isVisible = !newHiddenCategories.has(category);
          
          if (WebGL?.current?.viewer && task.meshId) {
            const meshObject = WebGL.current.viewer.meshesObject.get(task.meshId);
            if (meshObject) {
              UpdateGroupVisibility(meshObject, isVisible);
            }
          }
        }
        
        if (task.children) {
          updateVisibilityForCategory(task.children);
        }
      });
    };
    
    updateVisibilityForCategory(wbsStructure);
    var _v = WebGL && WebGL.current && WebGL.current.viewer;
    if (_v && _v.setVisibleByProgress) {
      _v.setVisibleByProgress(visibleIdsFromFlags(), { accumulate: false, frame: false });
    }

  };

  // Show/Hide all tasks
  const showAllTasks = () => {
    setHiddenCategories(new Set());
    resetAllVisibility(true);
    var _v = WebGL && WebGL.current && WebGL.current.viewer;
    if (_v && _v.setVisibleByProgress) {
      _v.setVisibleByProgress(visibleIdsFromFlags(), { accumulate: false, frame: false });
    }

  };

  const hideAllTasks = () => {
    const allCategories = new Set();
    tasks.forEach(task => {
      if (task.category) allCategories.add(task.category);
    });
    setHiddenCategories(allCategories);
    resetAllVisibility(false);
    var _v = WebGL && WebGL.current && WebGL.current.viewer;
    if (_v && _v.setVisibleByProgress) {
      _v.setVisibleByProgress(visibleIdsFromFlags(), { accumulate: false, frame: false });
    }

  };

  const resetAllVisibility = (visible = true) => {
    const resetVisibility = (tasks) => {
      return tasks.map(task => {
        task.visible = visible;
        
        if (WebGL?.current?.viewer && task.meshId) {
          const meshObject = WebGL.current.viewer.meshesObject.get(task.meshId);
          if (meshObject) {
            UpdateGroupVisibility(meshObject, visible);
          }
        }
        
        if (task.children) {
          task.children = resetVisibility(task.children);
        }
        
        return task;
      });
    };
    
    const updatedWbs = resetVisibility([...wbsStructure]);
    setWbsStructure(updatedWbs);
    setTasks(flattenTasks(updatedWbs));
    setActiveRules({});
    var _v = WebGL && WebGL.current && WebGL.current.viewer;
    if (_v && _v.setVisibleByProgress) {
      _v.setVisibleByProgress(visibleIdsFromFlags(), { accumulate: false, frame: false });
    }
resetAllMeshMaterials();
  };

  // Get unique categories
  const getCategories = () => {
    const categories = new Set();
    tasks.forEach(task => {
      if (task.category) categories.add(task.category);
    });
    return Array.from(categories);
  };

  const gridDates = getGridDates();
  
const liveToDate = React.useMemo(() => {
  if (!selectedTask) return null;
  const scoped = activeTasks.filter(
    t => t.isAWP || t.parentId === selectedTask.id || t.id === selectedTask.id
  );
  return computeActiveEstimatesToDate(scoped, currentDate);
}, [selectedTask, activeTasks, currentDate]);
  if (isLoading) {
    return (
      <div className="gantt-container">
        <div className="gantt-loading">
          <i className="fa fa-spinner fa-spin"></i>
          <span>Loading construction schedule...</span>
        </div>
      </div>
    );
  }






  return (
    <div className="gantt-container">
      <div className="gantt-header compact">
        <div className="gantt-controls">
          {/* Left Section - Search and Filters */}
          <div className="gantt-section gantt-filters">
            <div className="input-group input-group-sm">
              <div className="input-group-prepend">
                <span className="input-group-text">
                  <i className="fa fa-search"></i>
                </span>
              </div>
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            
            <select 
              className="form-control form-control-sm"
              value={filterMode} 
              onChange={(e) => setFilterMode(e.target.value)}
            >
              <option value="all">All</option>
              <option value="wbs">WBS</option>
              <option value="awp">AWP</option>
              <option value="visible">Visible</option>
              <option value="hidden">Hidden</option>
            </select>

            <div className="dropdown">
              <button 
                className="btn btn-sm btn-outline-secondary dropdown-toggle" 
                type="button" 
                data-toggle="dropdown"
              >
                <i className="fa fa-filter"></i>
              </button>
              <div className="dropdown-menu category-dropdown">
                <h6 className="dropdown-header">Categories</h6>
                {getCategories().map(category => (
                  <label key={category} className="dropdown-item">
                    <input
                      type="checkbox"
                      checked={!hiddenCategories.has(category)}
                      onChange={() => toggleCategoryVisibility(category)}
                      className="mr-2"
                    />
                    <span 
                      className="category-color-dot"
                      style={{ backgroundColor: getTaskColor(category) }}
                    ></span>
                    {category}
                  </label>
                ))}
              </div>
            </div>

            <div className="btn-group btn-group-sm">
              <button className="btn btn-sm btn-outline-primary" onClick={showAllTasks}>
                <i className="fa fa-eye"></i>
              </button>
              
              <button className="btn btn-sm btn-outline-secondary" onClick={hideAllTasks}>
                <i className="fa fa-eye-slash"></i>
              </button>
            </div>
          </div>

          {/* Center Section - Timeline Controls */}
          <div className="gantt-section gantt-timeline-controls">
            <div className="timeline-status">
              <span className="status-item">
  <i className="fa fa-exclamation-triangle text-danger"></i>
  <span>{activeRisk.toFixed(0)}%</span>
</span>
{activeRules?.riskPctNow != null && (
  <span className="status-item">
    <i className="fa fa-exclamation-triangle text-danger"></i>
    <span>{activeRules.riskPctNow.toFixed(1)}%</span>
  </span>
)}


              <span className="status-item">
                <i className="fa fa-calendar text-info"></i>
                <span>{formatDate(currentDate)}</span>
              </span>
              <span className="status-item">
                <i className="fa fa-dollar-sign text-success"></i>
                <span>{Currency}{(totalSpent/1000).toFixed(0)}k</span>
              </span>
              <span className="status-item">
                <i className="fa fa-tasks text-warning"></i>
                <span>{activeTasks.length}</span>
              </span>
              {processedMeshData.totalTime > 0 && (
                <span className="status-item" title="Total selected labor hours">
                  <i className="fa fa-clock-o text-primary"></i>
                  <span>{(processedMeshData.totalTime/8).toFixed(0)}d</span>
                </span>
              )}
              {Object.keys(activeRules).length > 0 && (
                <span className="status-item awp-active">
                  <i className="fa fa-exclamation-circle"></i>
                  <span>AWP</span>
                </span>
              )}
            </div>
            
            <div className="playback-controls">
              <button
                className={`btn btn-sm ${isPlaying ? 'btn-danger' : 'btn-success'}`}
                onClick={() => setIsPlaying(!isPlaying)}
              >
                <i className={`fa ${isPlaying ? 'fa-pause' : 'fa-play'}`}></i>
              </button>
              
              <button
                className="btn btn-sm btn-secondary"
              onClick={() => {
            setIsPlaying(false);
           // setCurrentDate(new Date(startDate));
          //  updateTimelinePosition(new Date(startDate));
            resetAllVisibility();
            resetAllMeshMaterials();
            showEntireScene(); 
            var v = WebGL && WebGL.current && WebGL.current.viewer;
            if (v) {
              if (v.stopAutoSpin) v.stopAutoSpin();
            //  if (v.setVisibleByProgress) v.setVisibleByProgress(visibleIdsForDate(startDate), { accumulate:false, frame:true });
            }

            }}

              >
                <i className="fa fa-stop"></i>
              </button>
              
              <select 
                className="form-control form-control-sm speed-selector"
                value={animationSpeed}
                onChange={(e) => setAnimationSpeed(Number(e.target.value))}
              >
                <option value={1}>1x</option>
                <option value={3}>3x</option>
                <option value={7}>7x</option>
                <option value={14}>14x</option>
              </select>
              
              {/* AWP Quick Jump */}
              <div className="dropdown ml-2">
                <button 
                  className="btn btn-sm btn-outline-info dropdown-toggle" 
                  type="button" 
                  data-toggle="dropdown"
                  title="Jump to AWP"
                >
                  <i className="fa fa-crosshairs"></i>
                </button>
                <div className="dropdown-menu">
                  <h6 className="dropdown-header">Jump to AWP</h6>
                  {Object.keys(awpMapping).map(awpCode => {
                    const awpTask = tasks.find(t => t.awpCode === awpCode);
                    if (!awpTask) return null;
                    
                    return (
                      <a 
                        key={awpCode}
                        className="dropdown-item"
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          setIsPlaying(false);
                          updateTimelinePosition(awpTask.startDate);
var v = WebGL && WebGL.current && WebGL.current.viewer;
if (v && v.setVisibleByProgress) {
  v.setVisibleByProgress(visibleIdsForDate(awpTask.startDate), { accumulate:false, frame:true });
}

                        }}
                      >
                        <span className="badge badge-danger mr-2">{awpCode}</span>
                        {awpMapping[awpCode].description}
                      </a>
                    );
                  })}
                </div>
              </div>
            </div>
            
            <div className="recording-controls">
              <button
                className={`btn btn-sm ${isRecording ? 'btn-danger recording-pulse' : 'btn-outline-danger'}`}
                onClick={() => {
                  setIsRecording(!isRecording);
                  if (!isRecording) {
                    recordedFrames.current = [];
                  }
                }}
                title={isRecording ? 'Stop Recording' : 'Start Recording'}
              >
                <i className="fa fa-video"></i>
              </button>
              
              <button
                className="btn btn-sm btn-outline-secondary"
                onClick={exportGif}
                disabled={recordedFrames.current.length === 0}
                title="Export GIF"
              >
                <i className="fa fa-image"></i>
              </button>
              
              <button
                className="btn btn-sm btn-outline-secondary"
                onClick={exportVideo}
                disabled={recordedFrames.current.length === 0}
                title="Export Video"
              >
                <i className="fa fa-download"></i>
              </button>
            </div>
          </div>

          {/* Right Section - View Mode */}
          <div className="gantt-section view-mode-selector">
            <div className="btn-group btn-group-sm">
              {[
                { mode: 'day', label: 'Day' },
                { mode: 'week', label: 'Week' },
                { mode: 'month', label: 'Month' }
              ].map(({ mode, label }) => (
                <button 
                  key={mode}
                  className={`btn btn-sm ${viewMode === mode ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setViewMode(mode)}
                  title={`View by ${label}`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className={`gantt-chart ${isMobile ? 'mobile' : ''}`} ref={ganttRef}>
        <div 
          className="gantt-sidebar" 
          ref={sidebarRef}
          style={{ width: isMobile ? '100%' : `${sidebarWidth}px` }}
        >
          <div className="gantt-sidebar-header">
            <span>Construction Schedule ({filteredTasks.length})</span>
            {jsonData && <span className="badge badge-success ml-2">Live</span>}
            {meshQuantityInfoRef?.current && meshQuantityInfoRef.current.length > 0 && (
              <span className="badge badge-info ml-2" title={`${meshQuantityInfoRef.current.length} components selected`}>
                <i className="fa fa-cube"></i> {meshQuantityInfoRef.current.length}
              </span>
            )}
          </div>
          <div className="gantt-task-list">
            {filteredTasks.map((task, index) => {
              const isActive = activeTasks.some(t => t.id === task.id);
              const taskDate = task.startDate;
              const isUpcoming = taskDate > currentDate;
              const isCompleted = task.endDate < currentDate;
              
              return (
                <div 
                  key={task.id} 
                  className={`gantt-task-row level-${task.level} ${selectedTask?.id === task.id ? 'selected' : ''} ${!task.visible ? 'hidden-task' : ''} ${task.isAWP ? 'awp-task' : ''} ${isActive ? 'active-task' : ''} ${isUpcoming ? 'upcoming-task' : ''} ${isCompleted ? 'completed-task' : ''}`}
                  style={{ paddingLeft: `${(task.level - 1) * 20 + 12}px` }}
                  onClick={() => handleTaskClick(task)}
                >
                  {task.children && task.children.length > 0 && (
                    <button 
                      className="expand-toggle"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleTaskExpansion(task.id);
                      }}
                    >
                      <i className={`fa ${task.expanded ? 'fa-chevron-down' : 'fa-chevron-right'}`}></i>
                    </button>
                  )}
                  
                  <button 
                    className="visibility-toggle"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleTaskVisibility(task);
                      if (WebGL?.current?.viewer) {
  syncSceneForDate(WebGL.current.viewer, tasks, currentDate);
}

                    }}
                  >
                    <i className={`fa ${task.visible ? 'fa-eye' : 'fa-eye-slash'}`}></i>
                  </button>
                  
                  <span className="wbs-code">{task.wbsCode}</span>
                  {task.isAWP && <span className="badge badge-danger ml-1">AWP</span>}
                  {isActive && <span className="badge badge-primary ml-1">Active</span>}
                  {isUpcoming && <span className="badge badge-secondary ml-1">Upcoming</span>}
                  {isCompleted && <span className="badge badge-success ml-1">Done</span>}
                  {task.category && (
                    <span 
                      className="category-indicator"
                      style={{ backgroundColor: getTaskColor(task.category) }}
                      title={task.category}
                    ></span>
                  )}
                  <span className="task-name">{task.name}</span>
                  {task.meshIds && task.meshIds.length > 0 && (
                    <span className="mesh-count" title={`${task.meshIds.length} linked components`}>
                      <i className="fa fa-cube"></i> {task.meshIds.length}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {!isMobile && (
          <div 
            className="resize-handle"
            onMouseDown={handleMouseDown}
          ></div>
        )}

        <div className="gantt-timeline" ref={timelineRef}>
          <div className="gantt-timeline-header">
            {gridDates.map((date, index) => (
              <div key={index} className="timeline-date">
                {formatDate(date)}
              </div>
            ))}
          </div>
          
          <div className="gantt-bars"
          onClick={(e) => {
    if (e.target === e.currentTarget) clearSelection();
  }}
  >
            {filteredTasks.map((task, index) => {
              const position = calculateTaskPosition(task);
              const isActive = activeTasks.some(t => t.id === task.id);
              return (
                <div key={task.id} className="gantt-bar-row">
                  <div 
                    className={`gantt-bar level-${task.level} ${selectedTask?.id === task.id ? 'selected' : ''} ${!task.visible ? 'hidden-bar' : ''} ${task.isAWP ? 'awp-bar' : ''} ${isActive ? 'active-bar' : ''}`}
                    style={{
                      ...position,
                      backgroundColor: task.color,
                      opacity: task.visible ? (hoveredTask?.id === task.id ? 0.8 : 1) : 0.3
                    }}
                    onClick={() => handleTaskClick(task)}
                    onMouseEnter={() => setHoveredTask(task)}
                    onMouseLeave={() => setHoveredTask(null)}
                  >
                    <span className="bar-duration">{task.duration}d</span>
{hoveredTask?.id === task.id && (
  <div className="task-tooltip">
    <div><strong>{task.name}</strong></div>
    <div>WBS: {task.wbsCode}</div>
    {task.isAWP && <div className="text-danger">Area Work Package</div>}
    <div>Duration: {task.duration} days</div>
    {task.isAWP && awpMapping?.[task.awpCode] && (
      <>
        <div>Cost-to-date: {Currency}{costToDate(task, currentDate).toFixed(0)}</div>
        <div>Est: {Currency}{(task.totalPrice||0).toFixed(0)}&nbsp;
          <small className="text-muted">
            (±{(awpMapping[task.awpCode].tolerancePct||0)}% =&nbsp;
            {Currency}{(minCostWithTol(task.totalPrice||0, awpMapping[task.awpCode].tolerancePct||0)).toFixed(0)}
            &nbsp;/&nbsp;
            {Currency}{(maxCostWithTol(task.totalPrice||0, awpMapping[task.awpCode].tolerancePct||0)).toFixed(0)})
          </small>
        </div>
      </>
    )}
  </div>
)}


                  </div>
                  
                  {task.level < 3 && task.children && (
                    <div 
                      className="progress-bar"
                      style={{
                        ...position,
                        width: `${parseFloat(position.width) * (task.progress || 0) / 100}%`,
                        backgroundColor: 'rgba(0, 0, 0, 0.2)',
                        height: '4px',
                        top: 'auto',
                        bottom: '3px'
                      }}
                    />
                  )}
                </div>
              );
            })}
            
            {/* Timeline Pointer */}
            <div 
              className="timeline-pointer"
              style={{ left: `${calculateTimelinePosition()}%` }}
              onMouseDown={(e) => {
                const handleDrag = (e) => handleTimelineDrag(e);
                const handleUp = () => {
                  document.removeEventListener('mousemove', handleDrag);
                  document.removeEventListener('mouseup', handleUp);
                };
                document.addEventListener('mousemove', handleDrag);
                document.addEventListener('mouseup', handleUp);
              }}
            >
              <div className="timeline-pointer-head"></div>
              <div className="timeline-pointer-label">
                {formatDate(currentDate)}
              </div>
              <div className="timeline-pointer-line"></div>
            </div>
          </div>
        </div>
      </div>

      {selectedTask && (
        <div className={`task-details ${isMobile ? 'mobile' : ''}`}>
<div className="task-details-header">
  <h4 className="mb-0">{selectedTask.name}</h4>
  <div className="btn-group btn-group-sm ml-auto">
    <button
      className="btn btn-sm btn-outline-secondary"
      onClick={() => toggleTaskVisibility(selectedTask)}
    >
      <i className={`fa ${selectedTask.visible ? 'fa-eye' : 'fa-eye-slash'}`} />
      {selectedTask.visible ? ' Hide' : ' Show'}
    </button>
    <button
      className="btn btn-sm btn-outline-dark"
      onClick={clearSelection}
      aria-label="Close details"
      title="Close details"
    >
      <i className="fa fa-times" />
    </button>
  </div>
</div>

          
          <div className="row">
            <div className="col-md-6">
              <div className="detail-row">
                <span className="detail-label">WBS Code:</span>
                <span className="detail-value">{selectedTask.wbsCode}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Duration:</span>
                <span className="detail-value">{selectedTask.duration} days</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Start Date:</span>
                <span className="detail-value">{formatDate(selectedTask.startDate)}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">End Date:</span>
                <span className="detail-value">{formatDate(selectedTask.endDate)}</span>
              </div>
              {selectedTask.dependencies && selectedTask.dependencies.length > 0 && (
                <div className="detail-row">
                  <span className="detail-label">Dependencies:</span>
                  <span className="detail-value">{selectedTask.dependencies.join(', ')}</span>
                </div>
              )}
            </div>
            
            <div className="col-md-6">
              {selectedTask.type && (
                <div className="detail-row">
                  <span className="detail-label">Type:</span>
                  <span className="detail-value">{selectedTask.type}</span>
                </div>
              )}
              {selectedTask.category && (
                <div className="detail-row">
                  <span className="detail-label">Category:</span>
                  <span className="detail-value">
                    <span 
                      className="category-color-dot"
                      style={{ backgroundColor: getTaskColor(selectedTask.category) }}
                    ></span>
                    {selectedTask.category}
                  </span>
                </div>
              )}
              {selectedTask.totalPrice > 0 && (
                <div className="detail-row">
                  <span className="detail-label">Total Cost:</span>
                  <span className="detail-value text-success">{Currency} {selectedTask.totalPrice.toFixed(2)}</span>
                </div>
              )}
              {selectedTask.isAWP && (
                <div className="detail-row">
                  <span className="detail-label">Package:</span>
                  <span className="detail-value">
                    <span className="badge badge-danger">Area Work Package</span>
                  </span>
                </div>
              )}
              {selectedTask.awpCode && awpRules[selectedTask.awpCode] && (
                <div className="detail-row">
                  <span className="detail-label">AWP Rule:</span>
                  <span className="detail-value">{awpRules[selectedTask.awpCode].name}</span>
                </div>
              )}
            </div>
          </div>
          
{/* Live To-Date (5D) */}
{liveToDate && (
  <div className="detail-section mt-3">
    <h5 className="mb-2">Live To-Date (5D)</h5>
    <div className="row">
      <div className="col-12">
        <CollapsibleEstimate
          type="labor"
          data={liveToDate.labor}
          priceView={true}
          currencysymb={Currency}
          timeIcon={iconTime}
          moneyIcon={iconMoney}
        />
        <CollapsibleEstimate
          type="material"
          data={liveToDate.material}
          priceView={true}
          currencysymb={Currency}
          timeIcon={null}
          moneyIcon={iconMoney}
          totalSelectionTime={liveToDate.totalSelectionTime}
          totalSelectionPrice={liveToDate.totalSelectionPrice}
        />
        <CollapsibleEstimate
          type="equipment"
          data={liveToDate.equip}
          priceView={true}
          currencysymb={Currency}
          timeIcon={iconTime}
          moneyIcon={iconMoney}
        />
      </div>
    </div>
  </div>
)}


          {/* Cost Breakdown */}
          {(selectedTask.laborCost > 0 || selectedTask.equipmentCost > 0 || selectedTask.materialCost > 0) && (
            <div className="detail-section mt-3">
              <h5 className="mb-2">Cost Breakdown</h5>
              <div className="row">
                <div className="col-4">
                  <div className="cost-item">
                    <i className="fa fa-users text-primary"></i>
                    <span className="cost-label">Labor</span>
                    <span className="cost-value">{Currency}{(selectedTask.laborCost || 0).toFixed(0)}</span>
                    {selectedTask.laborHours > 0 && (
                      <span className="cost-hours">{selectedTask.laborHours.toFixed(0)} hrs</span>
                    )}
                  </div>
                </div>
                <div className="col-4">
                  <div className="cost-item">
                    <i className="fa fa-cogs text-warning"></i>
                    <span className="cost-label">Equipment</span>
                    <span className="cost-value">{Currency}{(selectedTask.equipmentCost || 0).toFixed(0)}</span>
                    {selectedTask.equipmentHours > 0 && (
                      <span className="cost-hours">{selectedTask.equipmentHours.toFixed(0)} hrs</span>
                    )}
                  </div>
                </div>
                <div className="col-4">
                  <div className="cost-item">
                    <i className="fa fa-cube text-info"></i>
                    <span className="cost-label">Material</span>
                    <span className="cost-value">{Currency}{(selectedTask.materialCost || 0).toFixed(0)}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
          {/* Collapsible estimates (uses your component) */}
{selectedTask && (
  <div className="detail-section mt-3">
    <h5 className="mb-2">Estimates</h5>
    <CollapsibleEstimate
      type="labor"
      data={(selectedTask.laborInfo || []).map(l => ({
        TotalCost: Number(l.TotalPrice||0),
        TotalTime: Number(l.TotalTime||0),
        Code: selectedTask.name,
        Description: l.Description
      }))}
      priceView={true}
      currencysymb={Currency}
      timeIcon={iconTime}
      moneyIcon={iconMoney}
      icon={iconLabor}
      myClassName="modal-sideShare-table"
    />
    <CollapsibleEstimate
      type="equipment"
      data={(selectedTask.equipmentInfo || []).map(e => ({
        TotalCost: Number(e.TotalPrice||0),
        TotalTime: Number(e.TotalTime||0),
        Code: selectedTask.name,
        Description: e.Description
      }))}
      priceView={true}
      currencysymb={Currency}
      timeIcon={iconTime}
      moneyIcon={iconMoney}
      icon={iconEquip}
      myClassName="modal-sideShare-table"
    />
    <CollapsibleEstimate
      type="material"
      data={(selectedTask.materialInfo || [])}
      priceView={true}
      currencysymb={Currency}
      timeIcon={null}
      moneyIcon={iconMoney}
      icon={iconMat}
      totalSelectionTime={0}
      totalSelectionPrice={selectedTask.materialCost || 0}
      myClassName="modal-sideShare-table"
    />
  </div>
)}

          {/* Quantities */}
          {(selectedTask.area > 0 || selectedTask.volume > 0 || selectedTask.quantity > 0) && (
            <div className="detail-section mt-3">
              <h5 className="mb-2">Quantities</h5>
              <div className="row">
                {selectedTask.area > 0 && (
                  <div className="col-4">
                    <div className="sub-detail">
                      <span>Area:</span>
                      <span>{selectedTask.area.toFixed(2)} SF</span>
                    </div>
                  </div>
                )}
                {selectedTask.volume > 0 && (
                  <div className="col-4">
                    <div className="sub-detail">
                      <span>Volume:</span>
                      <span>{selectedTask.volume.toFixed(2)} CF</span>
                    </div>
                  </div>
                )}
                {selectedTask.quantity > 0 && (
                  <div className="col-4">
                    <div className="sub-detail">
                      <span>Quantity:</span>
                      <span>{selectedTask.quantity.toFixed(0)}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Labor Details */}
          {selectedTask.laborInfo && selectedTask.laborInfo.length > 0 && (
            <div className="detail-section mt-3">
              <h5 className="mb-2">Labor Activities ({selectedTask.laborInfo.length})</h5>
              <div className="labor-list">
                {selectedTask.laborInfo.slice(0, 5).map((labor, idx) => (
                  <div key={idx} className="sub-detail">
                    <span>{labor.Description || 'Labor Activity'}</span>
                    <span className="text-right">
                      {labor.TotalTime ? `${labor.TotalTime}h` : ''} 
                      {labor.TotalPrice ? ` • ${Currency}${labor.TotalPrice.toFixed(0)}` : ''}
                    </span>
                  </div>
                ))}
                {selectedTask.laborInfo.length > 5 && (
                  <div className="sub-detail text-muted">
                    <span>...and {selectedTask.laborInfo.length - 5} more activities</span>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Equipment Details */}
          {selectedTask.equipmentInfo && selectedTask.equipmentInfo.length > 0 && (
            <div className="detail-section mt-3">
              <h5 className="mb-2">Equipment ({selectedTask.equipmentInfo.length})</h5>
              <div className="equipment-list">
                {selectedTask.equipmentInfo.slice(0, 3).map((equip, idx) => (
                  <div key={idx} className="sub-detail">
                    <span>{equip.Description || 'Equipment'}</span>
                    <span className="text-right">
                      {equip.TotalTime ? `${equip.TotalTime}h` : ''} 
                      {equip.TotalPrice ? ` • ${Currency}${equip.TotalPrice.toFixed(0)}` : ''}
                    </span>
                  </div>
                ))}
                {selectedTask.equipmentInfo.length > 3 && (
                  <div className="sub-detail text-muted">
                    <span>...and {selectedTask.equipmentInfo.length - 3} more items</span>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Material Details */}
          {selectedTask.materialInfo && selectedTask.materialInfo.length > 0 && (
            <div className="detail-section mt-3">
              <h5 className="mb-2">Materials ({selectedTask.materialInfo.length})</h5>
              <div className="material-list">
                {selectedTask.materialInfo.slice(0, 5).map((mat, idx) => (
                  <div key={idx} className="sub-detail">
                    <span>{mat.Name || mat.Types || 'Material'}</span>
                    <span className="text-right">
                      {mat.TotalAmount ? `${mat.TotalAmount} ` : ''}
                      {mat.TotalPrice ? `${Currency}${mat.TotalPrice.toFixed(0)}` : ''}
                    </span>
                  </div>
                ))}
                {selectedTask.materialInfo.length > 5 && (
                  <div className="sub-detail text-muted">
                    <span>...and {selectedTask.materialInfo.length - 5} more items</span>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {selectedTask.crews && selectedTask.crews.length > 0 && (
            <div className="detail-section mt-3">
              <h5 className="mb-2">Assigned Crews</h5>
              {selectedTask.crews.map((crew, idx) => (
                <div key={idx} className="sub-detail">
                  <span>{crew.code}: {crew.description}</span>
                  <span>Yield: {crew.yield} {crew.yieldUnit}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      
      {/* Hidden canvas for recording */}
      <canvas ref={canvasRef} style={{ display: 'none' }} width={1920} height={1080} />
    </div>
  );
};

export default GanttChart;